import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState, useCallback } from "react";

export interface UsernameFieldProps {
  label: string;
  value: string;
  id?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  required?: boolean;
  requiredAsterisk?: boolean;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  title?: string;
  autocomplete?: "on" | "off";
  validationDelay?: number;
  customError?: string;
  apiError?: "conflict" | string;
  disabled?: boolean;
}

export const UsernameField: React.FC<UsernameFieldProps> = ({
  label,
  value,
  id,
  onChange,
  name,
  required = false,
  requiredAsterisk = true,
  minLength,
  maxLength,
  placeholder = "",
  title,
  autocomplete = "on",
  validationDelay = 500,
  customError,
  apiError,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [validationTimeout, setValidationTimeout] =
    useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (apiError === "conflict" && customError) {
      setError(customError);
      setTouched(true);
    }
  }, [apiError, customError]);

  const filterUsername = (input: string, isTyping: boolean = true): string => {
    let filtered = input.replace(/[^a-zA-Z0-9_.]/g, "");

    if (filtered.startsWith(".") || filtered.startsWith("_")) {
      filtered = filtered.substring(1);
    }

    if (!isTyping && (filtered.endsWith(".") || filtered.endsWith("_"))) {
      filtered = filtered.slice(0, -1);
    }

    filtered = filtered.replace(/\.{2,}/g, ".");

    filtered = filtered.replace(/_{2,}/g, "_");

    return filtered;
  };

  const validateUsername = useCallback(
    (value: string): string => {
      if (required && (!value || !value.trim())) {
        return "Este campo es obligatorio";
      }

      if (minLength && value.length < minLength) {
        return `Debe tener al menos ${minLength} caracteres`;
      }

      if (maxLength && value.length > maxLength) {
        return `No debe tener más de ${maxLength} caracteres`;
      }

      if (value && !/^[a-zA-Z0-9_.]+$/.test(value)) {
        return "Solo se permiten letras, números, guiones bajos (_) y puntos (.)";
      }

      return "";
    },
    [required, minLength, maxLength]
  );

  const debouncedValidation = useCallback(
    (value: string) => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }

      const timeout = setTimeout(() => {
        setError(validateUsername(value));
      }, validationDelay);

      setValidationTimeout(timeout);
    },
    [validationDelay, validateUsername, validationTimeout]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const originalValue = e.target.value;
    const filteredValue = filterUsername(originalValue, true);
    setInputValue(filteredValue);
    setTouched(true);
    setError("");

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: filteredValue,
        name: name || "",
      },
    } as React.ChangeEvent<HTMLInputElement>;

    if (required || filteredValue) {
      debouncedValidation(filteredValue);
    }

    onChange(syntheticEvent);
  };

  const handleBlur = () => {
    const finalValue = filterUsername(inputValue, false);

    if (finalValue !== inputValue) {
      setInputValue(finalValue);

      const syntheticEvent = {
        target: {
          value: finalValue,
          name: name || "",
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }

    if (!required && !finalValue) return;
    setTouched(true);
    if (validationTimeout) {
      clearTimeout(validationTimeout);
      setValidationTimeout(null);
    }
    setError(validateUsername(finalValue));
  };

  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  const getInputClassName = (baseClass: string) => {
    return `${baseClass} ${
      error && touched
        ? "border-2 border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    }`;
  };

  return (
    <div className="flex flex-col space-y-1">
      <label
        className="text-base font-medium text-gray-700"
        htmlFor={id || name}
      >
        {label}
        {required && requiredAsterisk && (
          <span className="text-red-500 ml-1" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          name={name}
          id={id || name}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          placeholder={placeholder}
          title={title}
          disabled={disabled}
          autoComplete={autocomplete}
          data-testid={id || name}
          className={getInputClassName(
            "w-full rounded-md py-2 px-3 transition-colors duration-200"
          )}
        />
        {error && touched && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          </div>
        )}
      </div>
      {error && touched && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <p className="mt-1 text-xs text-gray-500">
        Solo se permiten letras, números, guiones bajos (_) y puntos (.). Sin
        espacios.
        <br />
        <span className="font-medium">Ejemplo:</span> pescador123 o usuario.2024
      </p>
    </div>
  );
};
